'use es6';

import Actions from 'AdsUI/lib/Actions';
import { List, Map as ImmutableMap, Record, is } from 'immutable';
import { combineReducers } from 'redux';
import LeadSyncingEntityRecord from 'AdsUI/records/LeadSyncingEntityRecord';
import ExistingAdRecord from 'AdsUI/records/ad-management/ExistingAdRecord';
import KeywordCreationRecord from 'AdsUI/records/ad-management/KeywordCreationRecord.js';

const leadSyncingEntities = (state = ImmutableMap(), action) => {
  const {
    type,
    payload = {}
  } = action;

  switch (type) {
    case Actions.LEAD_AD_FETCH_LEAD_SYNCING_ENTITIES_SUCCEEDED:
      {
        const {
          network,
          accountId,
          entities
        } = payload;

        if (!entities) {
          return state;
        }

        return state.setIn([network, accountId], List(entities.map(LeadSyncingEntityRecord.fromCreationEntity)));
      }

    default:
      {
        return state;
      }
  }
};

const existingAds = (state = ImmutableMap(), action) => {
  switch (action.type) {
    case Actions.EXISTING_ADS_FETCH_SUCCEEDED:
      {
        const {
          accountId,
          network,
          campaignId,
          adGroupId,
          ads = []
        } = action.payload || {};
        return state.setIn([network, accountId, campaignId, adGroupId], List(ads.map(ad => new ExistingAdRecord(ad))));
      }

    default:
      {
        return state;
      }
  }
};

const existingKeywords = (state = ImmutableMap(), action) => {
  switch (action.type) {
    case Actions.EXISTING_KEYWORDS_FETCH_SUCCEEDED:
      {
        const {
          accountId,
          network,
          campaignId,
          adGroupId,
          keywords = []
        } = action.payload || {};
        return state.setIn([network, accountId, campaignId, adGroupId], List(keywords.map(({
          isNegative,
          keywordText,
          matchType
        }) => new KeywordCreationRecord({
          keywordText,
          matchType,
          negative: isNegative
        }))));
      }

    default:
      {
        return state;
      }
  }
}; // Deliberately stored outside of AdCreationRecord so that it isn't saved in drafts


const MarketingContactsCountStateRecord = new Record({
  fetched: false,
  fetching: false,
  polling: false,
  listPreviewId: null,
  listIds: null,
  nonMarketingContactsCount: 0
});

const marketingContactsCount = (state = MarketingContactsCountStateRecord(), action) => {
  switch (action.type) {
    case Actions.LEAD_AD_CREATION_STATE_SET:
    case Actions.LEAD_AD_CREATION_RESET:
    case Actions.LEAD_AD_CREATE_START:
    case Actions.AD_CREATION_MARKETING_CONTACTS_LIST_PREVIEW_CLEAR:
      {
        return MarketingContactsCountStateRecord();
      }

    case Actions.AD_CREATION_MARKETING_CONTACTS_LIST_PREVIEW_CREATE_START:
      {
        // This doesn't fire based on outside events, so it's ideal for
        // making sure we always have the most up-to-date list ids
        const {
          listIds
        } = action.payload;
        return MarketingContactsCountStateRecord({
          listIds,
          fetching: true
        });
      }

    case Actions.AD_CREATION_MARKETING_CONTACTS_LIST_PREVIEW_CREATE_SUCCEEDED:
      {
        if (!action.payload) {
          return state;
        }

        const {
          listIds,
          previewId
        } = action.payload;

        if (!is(listIds, state.listIds)) {
          return state;
        }

        return MarketingContactsCountStateRecord({
          listIds,
          fetched: true,
          listPreviewId: previewId,
          polling: true
        });
      }

    case Actions.AD_CREATION_MARKETING_CONTACTS_LIST_PREVIEW_POLL_SUCCEEDED:
      {
        if (!action.payload) {
          return state;
        }

        const {
          previewId,
          status,
          estimatedContacts,
          numPassedContacts
        } = action.payload;

        if (previewId !== state.listPreviewId) {
          return state;
        }

        if (status !== 'COMPLETE') {
          return state;
        }

        return state.merge({
          polling: false,
          nonMarketingContactsCount: numPassedContacts || estimatedContacts
        });
      }

    default:
      {
        return state;
      }
  }
};

export default combineReducers({
  leadSyncingEntities,
  existingAds,
  existingKeywords,
  marketingContactsCount
});